import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { CalendarDays, Utensils, Store, Wallet, Star, MapPin, Users, Mail, Send, X } from 'lucide-react';
import './ActivityTab.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return toISO(d); };

// Preset ranges. Weeks start on Monday to match the weekly digest email.
const PRESETS = [
  { key: 'thisWeek', label: 'This week', range: () => { const d = new Date(); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() - dow); return [toISO(d), toISO(new Date())]; } },
  { key: 'last7', label: 'Last 7 days', range: () => [daysAgo(6), toISO(new Date())] },
  { key: 'thisMonth', label: 'This month', range: () => { const d = new Date(); return [toISO(new Date(d.getFullYear(), d.getMonth(), 1)), toISO(d)]; } },
  { key: 'last30', label: 'Last 30 days', range: () => [daysAgo(29), toISO(new Date())] },
  { key: 'thisYear', label: 'This year', range: () => [`${new Date().getFullYear()}-01-01`, toISO(new Date())] },
  { key: 'all', label: 'All time', range: () => ['', ''] },
  { key: 'custom', label: 'Custom', range: null },
];

const fmtDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

const SummaryCard = ({ icon, value, label, testId }) => (
  <div className="activity-summary-card" data-testid={testId}>
    <div className="activity-summary-icon">{icon}</div>
    <div>
      <div className="activity-summary-value">{value}</div>
      <div className="activity-summary-label">{label}</div>
    </div>
  </div>
);

export const ActivityTab = ({ viewingAsUser, onRestaurantClick, userEmail }) => {
  const [preset, setPreset] = useState(() => localStorage.getItem('das.activityPreset') || 'last7');
  const [customFrom, setCustomFrom] = useState(daysAgo(29));
  const [customTo, setCustomTo] = useState(toISO(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailOpen, setEmailOpen] = useState(false);
  const [sendToMe, setSendToMe] = useState(true);
  const [extraEmails, setExtraEmails] = useState('');
  const [sending, setSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  const [fromDate, toDate] = useMemo(() => {
    if (preset === 'custom') return [customFrom, customTo];
    const p = PRESETS.find((x) => x.key === preset) || PRESETS[1];
    return p.range();
  }, [preset, customFrom, customTo]);

  useEffect(() => {
    localStorage.setItem('das.activityPreset', preset);
  }, [preset]);

  useEffect(() => {
    if (preset === 'custom' && fromDate && toDate && fromDate > toDate) {
      setError('"From" must be on or before "To".');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    const params = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (viewingAsUser?.email) params.impersonate_user = viewingAsUser.email;
    axios.get(`${API_URL}/api/activity`, { params })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.detail || 'Could not load your activity.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fromDate, toDate, preset, viewingAsUser]);

  const sendReport = async () => {
    const recipients = extraEmails.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
    if (sendToMe && userEmail) recipients.unshift(userEmail);
    if (recipients.length === 0) { setEmailStatus({ ok: false, text: 'Enter at least one email address.' }); return; }
    setSending(true);
    setEmailStatus(null);
    try {
      const params = {};
      if (viewingAsUser?.email) params.impersonate_user = viewingAsUser.email;
      const res = await axios.post(`${API_URL}/api/activity/email`, { from_date: fromDate || null, to_date: toDate || null, recipients }, { params });
      setEmailStatus({ ok: true, text: `${res.data.message}: ${res.data.recipients.join(', ')}` });
      setExtraEmails('');
    } catch (err) {
      setEmailStatus({ ok: false, text: err.response?.data?.detail || 'Could not send the report.' });
    } finally {
      setSending(false);
    }
  };

  const summary = data?.summary;
  const visits = data?.visits || [];
  const periodLabel = !fromDate && !toDate
    ? 'All time'
    : `${fromDate ? fmtDate(fromDate) : 'Beginning'} → ${toDate ? fmtDate(toDate) : 'Today'}`;

  return (
    <div className="activity-tab" data-testid="activity-tab">
      <div className="activity-header">
        <h1 className="activity-title">
          <CalendarDays size={24} style={{ color: '#2c5f2d' }} />
          My Activity
        </h1>
        <div className="activity-header-right">
          <span className="activity-period" data-testid="activity-period-label">{periodLabel}</span>
          <button
            type="button"
            className={`activity-email-btn ${emailOpen ? 'active' : ''}`}
            onClick={() => { setEmailOpen((v) => !v); setEmailStatus(null); }}
            data-testid="activity-email-toggle"
          >
            <Mail size={14} /> Email report
          </button>
        </div>
      </div>

      {emailOpen && (
        <div className="activity-email-panel" data-testid="activity-email-panel">
          <div className="activity-email-row">
            <label className="activity-email-me">
              <input type="checkbox" checked={sendToMe} onChange={(e) => setSendToMe(e.target.checked)} data-testid="activity-email-me" />
              Send to me{userEmail ? ` (${userEmail})` : ''}
            </label>
            <button type="button" className="activity-email-close" onClick={() => setEmailOpen(false)} aria-label="Close" data-testid="activity-email-close"><X size={14} /></button>
          </div>
          <div className="activity-email-row">
            <input
              type="text"
              className="activity-email-input"
              placeholder="Other addresses, separated by commas"
              value={extraEmails}
              onChange={(e) => setExtraEmails(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendReport(); } }}
              data-testid="activity-email-input"
            />
            <button type="button" className="activity-email-send" onClick={sendReport} disabled={sending || loading} data-testid="activity-email-send">
              <Send size={14} /> {sending ? 'Sending…' : `Send (${summary?.visits ?? 0} visit${summary?.visits === 1 ? '' : 's'})`}
            </button>
          </div>
          {emailStatus && (
            <div className={`activity-email-status ${emailStatus.ok ? 'ok' : 'err'}`} data-testid="activity-email-status">{emailStatus.text}</div>
          )}
        </div>
      )}

      <div className="activity-presets" role="tablist" data-testid="activity-presets">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            className={`activity-preset ${preset === p.key ? 'active' : ''}`}
            onClick={() => setPreset(p.key)}
            data-testid={`activity-preset-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {preset === 'custom' && (
        <div className="activity-custom-range" data-testid="activity-custom-range">
          <label>From
            <input type="date" value={customFrom} max={customTo || undefined} onChange={(e) => setCustomFrom(e.target.value)} data-testid="activity-from-date" />
          </label>
          <label>To
            <input type="date" value={customTo} min={customFrom || undefined} onChange={(e) => setCustomTo(e.target.value)} data-testid="activity-to-date" />
          </label>
        </div>
      )}

      {error && <div className="activity-error" data-testid="activity-error">{error}</div>}

      {summary && !error && (
        <div className="activity-summary" data-testid="activity-summary">
          <SummaryCard icon={<Utensils size={18} />} value={summary.visits} label={summary.visits === 1 ? 'visit' : 'visits'} testId="activity-summary-visits" />
          <SummaryCard icon={<Store size={18} />} value={summary.restaurants} label={summary.restaurants === 1 ? 'restaurant' : 'restaurants'} testId="activity-summary-restaurants" />
          <SummaryCard icon={<Wallet size={18} />} value={summary.total_spent > 0 ? `$${summary.total_spent.toFixed(2)}` : '—'} label="total spent" testId="activity-summary-spent" />
          <SummaryCard icon={<Star size={18} />} value={summary.avg_rating != null ? `${summary.avg_rating}/5` : '—'} label="avg rating" testId="activity-summary-rating" />
        </div>
      )}

      {loading && <p className="activity-muted" data-testid="activity-loading">Loading your visits…</p>}

      {!loading && !error && data && visits.length === 0 && (
        <p className="activity-muted" data-testid="activity-empty">No visits in this period. Time to dine out!</p>
      )}

      {!loading && visits.length > 0 && (
        <div className="activity-list" data-testid="activity-list">
          {visits.map((v) => (
            <div key={v.id} className={`activity-visit ${v.archived ? 'archived' : ''}`} data-testid={`activity-visit-${v.id}`}>
              <div className="activity-visit-top">
                <button
                  type="button"
                  className="activity-visit-name"
                  onClick={() => !v.orphaned && onRestaurantClick && onRestaurantClick(v.restaurantId)}
                  disabled={v.orphaned}
                  data-testid={`activity-visit-name-${v.id}`}
                >
                  {v.restaurantName}
                </button>
                {v.archived && <span className="closed-badge">Closed</span>}
                <span className="activity-visit-date">{fmtDate(v.visitDate)}</span>
              </div>
              <div className="activity-visit-details">
                {v.location && <span><MapPin size={12} /> {v.location}</span>}
                {v.cuisine && <span><Utensils size={12} /> {v.cuisine}</span>}
                {v.rating != null && <span><Star size={12} style={{ color: '#f59e0b' }} /> {v.rating}/5</span>}
                {v.cost != null && v.cost !== '' && <span><Wallet size={12} /> ${Number(v.cost).toFixed(2)}</span>}
                {v.guests && <span><Users size={12} /> {v.guests}</span>}
              </div>
              {(v.favoriteItems || v.notes) && (
                <div className="activity-visit-notes">
                  {v.favoriteItems && <div><strong>Favorites:</strong> {v.favoriteItems}</div>}
                  {v.notes && <div><strong>Notes:</strong> {v.notes}</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityTab;
